import { useContext, useEffect, useState } from "react";
import { AppContext } from "contexts/AppContext";
import SectionWrapper from "components/layouts/SectionWrapper";
import { Box, Grid2, Skeleton, useMediaQuery } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { callAPI } from "utils/API";
import Headline3 from "components/elements/Headline3";
import ContentNormal from "components/elements/ContentNormal";
import { shuffle } from "./util";
import Headline5 from "components/elements/Headline5";
import ArrowRightAlt from "components/icons/ArrowRightAlt";
import HorizontalScrollBlock from "components/blocks/horizontalScrollBlock/HorizontalScrollBlock";
import ButtonMedium4 from "components/elements/ButtonMedium4";

const BrandsSection = () => {
    const navigate = useNavigate()
    const isMobile = useMediaQuery('(max-width: 1200px)');
    const { accessibleShops, styleGuide } = useContext(AppContext)
    const [brands, setBrands] = useState([])
    const [isLoading, setIsLoading] = useState(true)
    const ShopID = accessibleShops && accessibleShops.length > 0 ? accessibleShops[0].ShopID : null

    const fetchBrands = async () => {
        const fetchedBrands = await callAPI(`/product/${ShopID}/manufacturerDetails`, "GET")
        if (fetchedBrands && fetchedBrands.length > 0) {
            const brandsToShow = fetchedBrands.filter(b => !!b.imageURL)
            shuffle(brandsToShow)
            setBrands(brandsToShow)
        }
        setIsLoading(false)
    }

    useEffect(() => {
        if (ShopID) {
            fetchBrands()
        }
    }, [ShopID])

    const colSize = { xs: 3 }

    const displayAmount = 3
    if (!isLoading && brands.length < displayAmount) {
        return null
    }

    const headlineBox = (
        <Box flex={1} display={'flex'} flexDirection={'column'} gap={2} justifyContent={'center'}>
            {isLoading
                ?
                <Box flex={1} display={'flex'} flexDirection={'column'} gap={2} justifyContent={'center'}>
                    <Skeleton />
                    <Skeleton />
                    <Skeleton width={'60%'} />
                </Box>
                :
                <Box flex={1} display={'flex'} flexDirection={'column'} gap={2} justifyContent={'center'}>
                    <Box display={'flex'} flexDirection={'column'} gap={1}>
                        <ContentNormal
                            translationKey="_HOME_PAGE_BRAND_SECTION_HEADLINE_1_"
                            fallbackText="Find dine favoritter"
                        />
                        <Headline3
                            translationKey="_HOME_PAGE_BRAND_SECTION_HEADLINE_2_"
                            fallbackText="Shop efter brand"
                        />
                    </Box>
                    <Box>
                        <ButtonMedium4
                            translationKey="_HOME_PAGE_BRAND_SECTION_BUTTON_TEXT_"
                            fallbackText="Se alle brands"
                            showIcon={false}
                            onClick={() => navigate(`/shop/${ShopID}`)}
                        />
                    </Box>
                </Box>
            }
        </Box>
    )

    return (
        <SectionWrapper>
            {isMobile
                ?
                <Box>
                    {headlineBox}
                    <HorizontalScrollBlock>
                        {brands.map(b => (
                            <Box maxWidth={300}>
                                <Brand key={b.CategoryID} brand={b} styleGuide={styleGuide} ShopID={ShopID} />
                            </Box>
                        ))}
                    </HorizontalScrollBlock>
                </Box>
                :
                <Box display={'flex'} alignItems={'center'}>
                    <Grid2 container spacing={'2%'} width={'100%'}>
                        <Grid2 size={colSize} alignContent={'center'}>
                            {headlineBox}
                        </Grid2>

                        {isLoading
                            ?
                            [...Array(displayAmount)].map((_, index) => (
                                <Grid2 key={index} size={colSize}>
                                    <Box display={'flex'} flexDirection={'column'} overflow={'hidden'} style={{ aspectRatio: 4 / 5 }}>
                                        <Skeleton variant="rectangular" height={'100%'} />
                                    </Box>
                                </Grid2>
                            ))
                            :
                            brands.slice(0, displayAmount).map(brand => (
                                <Grid2 key={brand.categoryName} size={colSize}>
                                    <Brand brand={brand} styleGuide={styleGuide} ShopID={ShopID} />
                                </Grid2>
                            ))
                        }

                    </Grid2>
                </Box>
            }
        </SectionWrapper>
    )
}

const Brand = (props) => {
    const navigate = useNavigate()
    const isMobile = useMediaQuery('(max-width: 900px)');
    const { styleGuide, brandData } = useContext(AppContext)
    const [hover, setHover] = useState(false);
    const { frontPageCategorySectionTextColor } = brandData?.features
    const textColor = frontPageCategorySectionTextColor || styleGuide.color7

    const toggleHover = (val) => {
        setHover(val);
    }

    return (
        <Box
            onClick={() => navigate(`/shop/${props.ShopID}?brand=${encodeURIComponent(props.brand.categoryName)}`)}
            onMouseEnter={() => toggleHover(true)}
            onMouseLeave={() => toggleHover(false)}
            gap={2}
            flex={1}
            display={'flex'}
            minWidth={150}
            flexDirection={'column'}
            overflow={'hidden'}
            style={{cursor: 'pointer'}}
        >
            {props.isLoading
                ?
                    <Box width={'100%'} style={{ aspectRatio: 4 / 5 }}>
                        <Skeleton variant="rectangular" width={'100%'} height={'100%'}  />
                    </Box> 
                :
                    <img src={props.brand.imageURL} style={{ objectFit: 'cover', aspectRatio: 4/5, backgroundColor: props.styleGuide.color3, overflow: 'hidden' }} />
            }

            {props.isLoading
                ?
                    <Box display={'flex'} flexDirection={'column'} gap={'4px'}>
                        <Skeleton width={'90%'} />
                        <Skeleton width={'60%'} height={30} />
                    </Box>
                :
                    <Box display={'flex'} flexDirection={'column'} gap={'4px'}>
                        <ContentNormal
                            style={{fontSize: {xs: 14, md: styleGuide.contentNormalFontSize}, color: textColor}}
                            fallbackText={props.brand.categoryDescription || ' '}
                        />
                        <Box display={'flex'} alignItems={'center'} gap={hover ? 4 : 2} style={{transition: 'all .3s ease-in-out'}}>
                            <Headline5
                                style={{fontSize: {xs: 14, md: styleGuide.headline5FontSize}, color: textColor}}
                                fallbackText={props.brand.categoryName || ' '}
                            />
                            {!isMobile && <ArrowRightAlt fill={textColor} />}
                        </Box>
                    </Box>
            }
            
        </Box>
    )
}

export default BrandsSection